import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import autoAnimate from '@formkit/auto-animate';

import { TextInput as StandardTextInput, FormElement, Label } from '@dashboard/components/Form';

import Button from '@dashboard/components/Button';

import { Checkbox, TextInput } from '@dashboard/components/Formik';

import { get, isEmpty } from 'lodash';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { X } from 'lucide-react';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import DialogOld from '@dashboard/components/DialogOld';
import Link from '@dashboard/components/Link';
import { usePreview } from '@dashboard/context/PreviewContext';
import { confirm } from '@dashboard/lib/confirm';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import usePage from '@/dashboard/hooks/usePage';
import { hasOneTimePrice, hasRecurringPrice } from '@dashboard/services/getBillingModel';
import { PAGE_TYPES } from '@/dashboard/enums/page-types';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const getRedirectBase = redirectUrl => {
  const trimmed = redirectUrl.endsWith('/') ? redirectUrl.slice(0, -1) : redirectUrl;

  return trimmed;
};

const getRedirectUrlPath = (checkout, values, deleteVariable) =>
  values.redirectUrlPath.map((path, index) => {
    let part;
    if (path.key === 'fields') {
      part = get(checkout, 'fields')?.find(el => el._id === path.identifier);
    } else if (path.key === 'variants') {
      part = get(checkout.product, 'variants')?.find(el => el._id === path.identifier);
    } else if (path.key === 'coupon') {
      part = path;
    } else if (path.key === 'orderId') {
      part = path;
    } else if (path.key === 'stripeCustomerId') {
      part = path;
    } else if (path.key === 'stripePaymentId') {
      part = path;
    } else if (path.key === 'stripeSubscriptionId') {
      part = path;
    }

    if (!part) return '';

    return (
      <Button
        handleClick={() => deleteVariable(path._id, 'redirectUrlPath')}
        appearance="bare"
        className="flex items-center py-0 !px-0"
        key={index}
      >
        {path.key === 'fields' ? `{${part.label}}` : ''}

        {path.key === 'variants' ? `{${part.name}}` : ''}

        {path.key === 'coupon' ? `{Coupon code}` : ''}

        {path.key === 'orderId' ? `{Order ID}` : ''}

        {path.key === 'stripeCustomerId' ? `{Stripe Customer ID}` : ''}

        {path.key === 'stripePaymentId' ? `{Stripe Payment ID}` : ''}

        {path.key === 'stripeSubscriptionId' ? `{Stripe Subscription ID}` : ''}

        {index !== values.redirectUrlPath.length - 1 ? '/' : ''}
        <X className="h-4 w-4 ml-0.5 -mt-1" />
      </Button>
    );
  });

const getRedirectUrlQuery = (checkout, values, deleteVariable) =>
  values.redirectUrlQuery.map((query, index) => {
    let part;
    if (query.key === 'fields') {
      part = get(checkout, 'fields')?.find(el => el._id === query.identifier);
    } else if (query.key === 'variants') {
      part = get(checkout.product, 'variants')?.find(el => el._id === query.identifier);
    } else if (query.key === 'coupon') {
      part = query;
    } else if (query.key === 'orderId') {
      part = query;
    } else if (query.key === 'stripeCustomerId') {
      part = query;
    } else if (query.key === 'stripePaymentId') {
      part = query;
    } else if (query.key === 'stripeSubscriptionId') {
      part = query;
    }

    if (!part) return '';

    return (
      <Button
        handleClick={() => deleteVariable(query._id, 'redirectUrlQuery')}
        appearance="bare"
        key={index}
        className="flex items-center py-0 !px-0"
      >
        {query.key === 'fields' && `${query.parameter}={${part.label}}`}

        {query.key === 'variants' && `${query.parameter}={${part.name}}`}

        {query.key === 'coupon' && `${query.parameter}={Coupon code}`}

        {query.key === 'orderId' && `${query.parameter}={Order ID}`}

        {query.key === 'stripeCustomerId' && `${query.parameter}={Stripe Customer ID}`}

        {query.key === 'stripePaymentId' && `${query.parameter}={Stripe Payment ID}`}

        {query.key === 'stripeSubscriptionId' && `${query.parameter}={Stripe Subscription ID}`}

        {index !== values.redirectUrlQuery.length - 1 ? '&' : ''}

        <X className="h-4 w-4 ml-0.5 -mt-1" />
      </Button>
    );
  });

const getCompleteRedirectUrl = (checkout, values, deleteVariable) => {
  if (!values.redirectUrl) return '';

  const base = getRedirectBase(values.redirectUrl);
  const paths = getRedirectUrlPath(checkout, values, deleteVariable);
  const query = getRedirectUrlQuery(checkout, values, deleteVariable);

  const parts = [base];
  if (paths && paths.length > 0) {
    parts.push('/');
    parts.push(paths);
  }

  if (query && query.length > 0) {
    // if the base already has a query string, append the query with an ampersand
    const hasQueryString = base.includes('?');
    parts.push(hasQueryString ? '&' : '?');

    parts.push(query);
  }

  return parts.map(part => part);
};

const RedirectAfterPaymentForm = () => {
  const { page, updatePage } = usePage();
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  return (
    <Formik
      initialValues={{
        afterPaymentAction: page.afterPaymentAction,
        redirectUrl: page.redirectUrl,
        redirectUrlQuery: page.redirectUrlQuery,
        redirectUrlPath: page.redirectUrlPath,
        redirectUrlInsideEmbed: page.redirectUrlInsideEmbed,
        showCheckoutConfirmationBeforeRedirect: page.showCheckoutConfirmationBeforeRedirect,
        allowDynamicRedirectUrl: page.allowDynamicRedirectUrl,
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          if (data.afterPaymentAction === AfterPaymentActionEnum.REDIRECT) {
            setPreview({
              type: 'checkout',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              afterPaymentAction: data.afterPaymentAction,
              redirectUrl: data.redirectUrl,
              redirectUrlQuery: data.redirectUrlQuery,
              redirectUrlPath: data.redirectUrlPath,
              redirectUrlInsideEmbed: data.redirectUrlInsideEmbed,
              showCheckoutConfirmationBeforeRedirect: data.showCheckoutConfirmationBeforeRedirect,
              allowDynamicRedirectUrl: data.allowDynamicRedirectUrl,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, isSubmitting }) => {
        useEffect(() => {
          setFieldValue('afterPaymentAction', AfterPaymentActionEnum.REDIRECT);
        }, []);

        return (
          <Form onSubmit={handleSubmit}>
            <UnsavedChangesReporter />
            <FormElement>
              <RedirectUrlEditor
                values={values}
                page={page}
                name="redirectUrl"
                setFieldValue={setFieldValue}
              />

              <FormError name="redirectUrl" />
            </FormElement>

            <FormElement>{status && status}</FormElement>

            <FormSubmit>
              <FormSubmit.Button>Save</FormSubmit.Button>
              <FormSubmit.State visible={!!dirty && !isSubmitting} />
            </FormSubmit>
          </Form>
        );
      }}
    </Formik>
  );
};

const RedirectUrlEditor = ({ page, values, setFieldValue }) => {
  const { seller } = useAuth();

  const deleteVariable = async (id, collection) => {
    const proceed = await confirm('Delete this variable?', {
      confirm: 'Delete',
    });

    if (!proceed) {
      return;
    }

    setFieldValue(
      collection,
      values[collection].filter(val => val._id !== id),
    );
  };

  const parent = useRef(null);

  useEffect(() => {
    parent.current && autoAnimate(parent.current, { duration: 100, easing: 'ease' });
  }, [parent]);

  const redirectUrlPreview = getCompleteRedirectUrl(page, values, deleteVariable);

  const pageUrl = getCheckoutUrl(page, seller);

  return (
    <>
      <FormElement>
        <Label htmlFor="redirectUrl">Redirect URL</Label>
        <TextInput
          id="redirectUrl"
          name="redirectUrl"
          type="url"
          placeholder="https://"
          value={values.redirectUrl || ''}
          required
        />
      </FormElement>

      <Disclosure
        ref={parent}
        as="div"
        defaultOpen={!isEmpty(values.redirectUrlPath) || !isEmpty(values.redirectUrlQuery)}
      >
        <DisclosureButton className="flex items-center gap-x-1 mt-4 text-gray-700 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground font-medium text-sm">
          {({ open }) => (
            <>
              <span>Pre-fill checkout fields & add variables to URL</span>
              {open ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </>
          )}
        </DisclosureButton>
        <DisclosurePanel className="mt-4">
          <div className="bg-gray-50 dark:bg-card rounded p-2 break-all">
            {redirectUrlPreview || 'First enter a Redirect URL in the field above'}
          </div>

          <RedirectUrlPathEditor page={page} values={values} setFieldValue={setFieldValue} />

          <RedirectUrlQueryEditor page={page} values={values} setFieldValue={setFieldValue} />

          <ul className="list-disc ml-4 text-sm space-y-1 mt-4">
            <li>
              <Link
                to="/docs/articles/add-field-and-variant-data-to-redirect-url"
                target="_blank"
                appearance="light"
              >
                How to add field and variant data to redirect URL
              </Link>
            </li>
            <li>
              <Link to="/docs/articles/prefill-checkout-fields" target="_blank" appearance="light">
                How to pre-fill checkout fields
              </Link>
            </li>
          </ul>
        </DisclosurePanel>
      </Disclosure>

      <Disclosure ref={parent} as="div" defaultOpen={!!values.allowDynamicRedirectUrl}>
        <DisclosureButton className="flex items-center gap-x-1 mt-4 text-gray-700 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground font-medium text-sm">
          {({ open }) => (
            <>
              <span>Additional settings</span>
              {open ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </>
          )}
        </DisclosureButton>

        <DisclosurePanel className="mt-4">
          <FormElement>
            <Checkbox.Group>
              <Checkbox.Wrapper>
                <Checkbox id="allowDynamicRedirectUrl" name="allowDynamicRedirectUrl" />
                <Checkbox.Label htmlFor="allowDynamicRedirectUrl">
                  Dynamic redirect URL{' '}
                  <InfoTooltip>
                    If enabled, you can pass a redirect URL as a query parameter to the checkout
                    URL. Use the parameter{' '}
                    <code>
                      <i>redirect_url</i>
                    </code>
                    . For example, <code>{pageUrl}?redirect_url=https://example.com</code>
                  </InfoTooltip>
                  <Checkbox.LabelDescription>
                    You also need to add a Redirect URL to the field above, which will be used if no
                    redirect URL is passed as a query parameter.
                  </Checkbox.LabelDescription>
                </Checkbox.Label>
              </Checkbox.Wrapper>

              <Checkbox.Wrapper>
                <Checkbox id="redirectUrlInsideEmbed" name="redirectUrlInsideEmbed" />
                <Checkbox.Label htmlFor="redirectUrlInsideEmbed">
                  Redirect URL inside embed frame{' '}
                  <InfoTooltip>
                    If you&apos;re embedding the checkout, redirect inside the embed frame instead
                    of redirecting the main browser window.
                  </InfoTooltip>
                </Checkbox.Label>
              </Checkbox.Wrapper>
            </Checkbox.Group>
          </FormElement>
        </DisclosurePanel>
      </Disclosure>
    </>
  );
};

const RedirectUrlPathEditor = ({ page, values, setFieldValue }) => {
  const [isEditing, setIsEditing] = useState(false);

  const onButtonClick = (key, identifier) => {
    setFieldValue('redirectUrlPath', [...values.redirectUrlPath, { key, identifier }]);

    setIsEditing(false);
  };

  return (
    <>
      <Button
        handleClick={() => setIsEditing(!isEditing)}
        appearance="light"
        style={{ marginTop: '0.5em', marginRight: '0.5em' }}
        type="button"
        size="small"
      >
        + Add Path Variable
      </Button>

      <DialogOld isOpen={isEditing} onClose={() => setIsEditing(false)} title="Add Path Variable">
        <>
          <div className="mb-4">
            <h4 className="mb-2">Fields</h4>
            {page.fields.map(field => (
              <div key={field._id}>
                <Button handleClick={() => onButtonClick('fields', field._id)} appearance="bare">
                  {field.label}
                </Button>
              </div>
            ))}

            {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.EVENT) &&
              page.showCouponCodeField && (
                <div>
                  <Button handleClick={() => onButtonClick('coupon', 'coupon')} appearance="bare">
                    Coupon code
                  </Button>
                </div>
              )}
          </div>

          {page.type === PAGE_TYPES.CHECKOUT && page.product.variants.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2">Variant SKUs</h4>
              {page.product.variants.map(variant => (
                <div key={variant._id}>
                  <Button
                    handleClick={() => onButtonClick('variants', variant._id)}
                    appearance="bare"
                  >
                    {variant.name}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.EVENT) && (
            <div>
              <h4 className="mb-2">Payment details</h4>

              <div>
                <Button handleClick={() => onButtonClick('orderId', 'orderId')} appearance="bare">
                  Order ID
                </Button>
              </div>

              <div>
                <Button
                  handleClick={() => onButtonClick('stripeCustomerId', 'stripeCustomerId')}
                  appearance="bare"
                >
                  Stripe Customer ID
                </Button>
              </div>

              {(page.type === PAGE_TYPES.EVENT ||
                (page.type === PAGE_TYPES.CHECKOUT && hasOneTimePrice(page.product))) && (
                <div>
                  <Button
                    handleClick={() => onButtonClick('stripePaymentId', 'stripePaymentId')}
                    appearance="bare"
                  >
                    Stripe Payment ID
                  </Button>
                </div>
              )}

              {page.type === PAGE_TYPES.CHECKOUT && hasRecurringPrice(page.product) && (
                <div>
                  <Button
                    handleClick={() =>
                      onButtonClick('stripeSubscriptionId', 'stripeSubscriptionId')
                    }
                    appearance="bare"
                  >
                    Stripe Subscription ID
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      </DialogOld>
    </>
  );
};
const RedirectUrlQueryEditor = ({ page, values, setFieldValue }) => {
  const [parameter, setParameter] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const onButtonClick = (key, identifier) => {
    if (!parameter) {
      setError('A parameter is required');
      return;
    }

    setFieldValue('redirectUrlQuery', [...values.redirectUrlQuery, { key, identifier, parameter }]);

    setIsEditing(false);
  };

  return (
    <>
      <Button
        handleClick={() => setIsEditing(!isEditing)}
        appearance="light"
        style={{ marginTop: '0.5em' }}
        type="button"
        size="small"
      >
        + Add Query Variable
      </Button>

      <DialogOld isOpen={isEditing} onClose={() => setIsEditing(false)} title="Add Query Variable">
        <>
          <div className="mb-4">
            <Label>
              Parameter{' '}
              <InfoTooltip>
                <p className="text-sm">
                  The parameter name that will be used in the URL. For example, if you enter{' '}
                  <code>
                    <i>email_address</i>
                  </code>
                  , the URL will be{' '}
                  <code>
                    https://example.com?<i>email_address</i>=example@gmail.com
                  </code>
                  .
                </p>
              </InfoTooltip>
            </Label>
            <StandardTextInput
              onChange={e => setParameter(e.target.value)}
              name="parameter"
              value={parameter}
              placeholder="parameter"
              border
            />
            <FormErrorBase>{error}</FormErrorBase>
          </div>
          <div className="mb-4">
            <h4 className="mb-2">Fields</h4>
            {page.fields.map(field => (
              <div key={field._id}>
                <Button handleClick={() => onButtonClick('fields', field._id)} appearance="bare">
                  {field.label}
                </Button>
              </div>
            ))}

            {page.showCouponCodeField && (
              <div>
                <Button handleClick={() => onButtonClick('coupon', 'coupon')} appearance="bare">
                  Coupon code
                </Button>
              </div>
            )}
          </div>

          {page.type === PAGE_TYPES.CHECKOUT && page.product.variants.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2">Variant SKUs</h4>
              {page.product.variants.map(variant => (
                <div key={variant._id}>
                  <Button
                    handleClick={() => onButtonClick('variants', variant._id)}
                    appearance="bare"
                  >
                    {variant.name}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.EVENT) && (
            <div>
              <h4 className="mb-2">Payment details</h4>

              <div>
                <Button handleClick={() => onButtonClick('orderId', 'orderId')} appearance="bare">
                  Order ID
                </Button>
              </div>

              <div>
                <Button
                  handleClick={() => onButtonClick('stripeCustomerId', 'stripeCustomerId')}
                  appearance="bare"
                >
                  Stripe Customer ID
                </Button>
              </div>

              {(page.type === PAGE_TYPES.EVENT ||
                (page.type === PAGE_TYPES.CHECKOUT && hasOneTimePrice(page.product))) && (
                <div>
                  <Button
                    handleClick={() => onButtonClick('stripePaymentId', 'stripePaymentId')}
                    appearance="bare"
                  >
                    Stripe Payment ID
                  </Button>
                </div>
              )}

              {page.type === PAGE_TYPES.CHECKOUT && hasRecurringPrice(page.product) && (
                <div>
                  <Button
                    handleClick={() =>
                      onButtonClick('stripeSubscriptionId', 'stripeSubscriptionId')
                    }
                    appearance="bare"
                  >
                    Stripe Subscription ID
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      </DialogOld>
    </>
  );
};

export default RedirectAfterPaymentForm;
